import { useState, useCallback } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  UsersRound,
  MoreHorizontal,
  Users,
  CheckCircle,
  XCircle,
  Plus,
  ListPlus,
  Loader2,
  Pencil,
  Trash2,
  RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SearchInput } from "@/components/ui/search-input";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Pagination } from "@/components/ui/pagination";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { usePermissions } from "@/hooks/usePermissions";
import { groupsApi, Group } from "@/services/api/groups";
import { usePaginatedData, PaginatedResponse } from "@/hooks/usePaginatedData";
import { GroupBulkCreateDialog } from "@/components/portal/platform/GroupBulkCreateDialog";

export const Route = createFileRoute("/portal/platform/groups")({
  component: GroupsPage,
});

interface FormData {
  name: string;
  description: string;
}

function GroupsPage() {
  const { token } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const { can, isAdmin } = usePermissions();
  const navigate = useNavigate();

  const organisationId = selectedOrganisation?.organisationId;

  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // Dialog state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showBulkCreateDialog, setShowBulkCreateDialog] = useState(false);
  const [editingGroup, setEditingGroup] = useState<Group | null>(null);
  const [deletingGroup, setDeletingGroup] = useState<Group | null>(null);

  // Form state
  const [formData, setFormData] = useState<FormData>({
    name: "",
    description: "",
  });
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  // Server-side paginated data fetch
  const fetchGroups = useCallback(
    async (
      page: number,
      pageSize: number,
    ): Promise<PaginatedResponse<Group>> => {
      if (!token || !organisationId) {
        return { data: [], totalCount: 0, page, pageSize };
      }
      const response = await groupsApi.listGroups(token, organisationId, {
        offset: (page - 1) * pageSize,
        limit: pageSize,
        search: searchTerm || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
        include_inactive: true,
      });
      return {
        data: response.items || [],
        totalCount: response.total || 0,
        page,
        pageSize,
      };
    },
    [token, organisationId, searchTerm, sortBy, sortOrder],
  );

  const {
    data: groups,
    isLoading,
    error,
    pagination,
    pageSizeOptions,
    goToPage,
    nextPage,
    prevPage,
    firstPage,
    lastPage,
    setPageSize,
    refresh,
    canGoNext,
    canGoPrev,
    startIndex,
    endIndex,
  } = usePaginatedData<Group>({
    fetchFn: fetchGroups,
    initialPageSize: 25,
    dependencies: [token, organisationId, searchTerm, sortBy, sortOrder],
  });

  function handleSort(column: string) {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  }

  function getSortDirection(column: string): "asc" | "desc" | null {
    return sortBy === column ? sortOrder : null;
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Form handlers
  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
    });
  };

  const openCreateDialog = () => {
    resetForm();
    setShowCreateDialog(true);
  };

  const openEditDialog = (group: Group) => {
    setFormData({
      name: group.name,
      description: group.description || "",
    });
    setEditingGroup(group);
  };

  const closeDialogs = () => {
    setShowCreateDialog(false);
    setEditingGroup(null);
    resetForm();
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !organisationId) return;

    setFormSubmitting(true);
    try {
      if (editingGroup) {
        await groupsApi.updateGroup(token, organisationId, editingGroup.id, {
          name: formData.name,
          description: formData.description || undefined,
        });
        toast.success("Group updated successfully");
      } else {
        await groupsApi.createGroup(token, organisationId, {
          name: formData.name,
          description: formData.description || undefined,
        });
        toast.success("Group created successfully");
      }
      closeDialogs();
      refresh();
    } catch (err) {
      console.error("Failed to save group:", err);
      toast.error(
        editingGroup ? "Failed to update group" : "Failed to create group",
      );
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!token || !organisationId || !deletingGroup) return;

    setDeleteSubmitting(true);
    try {
      await groupsApi.deleteGroup(token, organisationId, deletingGroup.id);
      toast.success("Group deleted successfully");
      setDeletingGroup(null);
      refresh();
    } catch (err) {
      console.error("Failed to delete group:", err);
      toast.error("Failed to delete group");
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const handleViewGroup = (group: Group) => {
    navigate({
      to: "/portal/platform/groups/$groupId",
      params: { groupId: group.id },
    });
  };

  // Permission checks
  const canCreate = can("create", "groups") || isAdmin;
  const canEdit = can("edit", "groups") || isAdmin;
  const canDelete = can("delete", "groups") || isAdmin;

  // Calculate stats from current page data
  const activeCount = groups.filter((g) => g.is_active).length;
  const inactiveCount = groups.filter((g) => !g.is_active).length;

  if (!organisationId) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl font-bold text-slate-900">Groups</h1>
          <p className="mt-2 text-lg text-slate-600">
            Manage feature access groups for your organisation
          </p>
        </div>
        <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
          <div className="py-12 text-center">
            <p className="text-muted-foreground">
              Please select an organisation to manage groups.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl font-bold text-slate-900">Groups</h1>
          <p className="mt-2 text-lg text-slate-600">
            Manage feature access groups for your organisation
          </p>
        </div>
        <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
          <div className="py-12 text-center">
            <p className="text-destructive">Failed to load groups: {error}</p>
            <Button onClick={refresh} variant="outline" className="mt-4">
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900">Groups</h1>
          <p className="mt-2 text-lg text-slate-600">
            Manage feature access groups for your organisation
          </p>
        </div>
        {canCreate && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setShowBulkCreateDialog(true)}
              className="rounded-full"
            >
              <ListPlus className="mr-2 h-4 w-4" />
              Bulk create
            </Button>
            <Button onClick={openCreateDialog} className="rounded-full">
              <Plus className="mr-2 h-4 w-4" />
              Create Group
            </Button>
          </div>
        )}
      </div>

      {/* Content Panel */}
      <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm space-y-6">
        {/* Controls bar */}
        <div className="flex flex-wrap items-center gap-3">
          <SearchInput
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClear={() => setSearchTerm("")}
            placeholder="Search groups..."
            containerClassName="flex-1 min-w-[200px]"
          />
          <Button variant="outline" size="sm" onClick={refresh}>
            {isLoading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Groups</CardTitle>
              <UsersRound className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{pagination.totalCount}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Active (this page)
              </CardTitle>
              <CheckCircle className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{activeCount}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Inactive (this page)
              </CardTitle>
              <XCircle className="h-4 w-4 text-red-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{inactiveCount}</div>
            </CardContent>
          </Card>
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : groups.length === 0 ? (
          <div className="text-center py-12">
            <UsersRound className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">
              {searchTerm ? "No groups match your search" : "No groups yet"}
            </h3>
            <p className="text-muted-foreground mb-4">
              {searchTerm
                ? "Try a different search term."
                : "Create your first group to control feature access."}
            </p>
            {!searchTerm && canCreate && (
              <Button onClick={openCreateDialog}>
                <Plus className="h-4 w-4 mr-2" />
                Create Group
              </Button>
            )}
          </div>
        ) : (
          <>
            <Table>
                <TableHeader>
                  <TableRow>
                    <SortableTableHead
                      direction={getSortDirection("name")}
                      onSort={() => handleSort("name")}
                    >
                      Group
                    </SortableTableHead>
                    <SortableTableHead
                      direction={getSortDirection("is_active")}
                      onSort={() => handleSort("is_active")}
                    >
                      Status
                    </SortableTableHead>
                    <SortableTableHead
                      direction={getSortDirection("member_count")}
                      onSort={() => handleSort("member_count")}
                    >
                      Members
                    </SortableTableHead>
                    <SortableTableHead
                      direction={getSortDirection("created_at")}
                      onSort={() => handleSort("created_at")}
                    >
                      Created
                    </SortableTableHead>
                    <SortableTableHead
                      direction={null}
                      onSort={() => {}}
                      disabled
                      className="w-[50px]"
                    >
                      {" "}
                    </SortableTableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {groups.map((group) => (
                    <TableRow
                      key={group.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleViewGroup(group)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center">
                            <UsersRound className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <div className="font-medium">{group.name}</div>
                            {group.description && (
                              <div className="text-sm text-muted-foreground line-clamp-1">
                                {group.description}
                              </div>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Badge
                          variant={group.is_active ? "success" : "destructive"}
                        >
                          {group.is_active ? (
                            <span className="flex items-center">
                              <CheckCircle className="h-3 w-3 mr-1" /> Active
                            </span>
                          ) : (
                            <span className="flex items-center">
                              <XCircle className="h-3 w-3 mr-1" /> Inactive
                            </span>
                          )}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Users className="h-4 w-4 text-muted-foreground" />
                          <span>{group.member_count ?? 0}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {formatDate(group.created_at)}
                        </span>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => handleViewGroup(group)}
                            >
                              <Users className="h-4 w-4 mr-2" />
                              Manage Members
                            </DropdownMenuItem>
                            {canEdit && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => openEditDialog(group)}
                                >
                                  <Pencil className="h-4 w-4 mr-2" />
                                  Edit Group
                                </DropdownMenuItem>
                              </>
                            )}
                            {canDelete && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => setDeletingGroup(group)}
                                  className="text-destructive focus:text-destructive"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete Group
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

            {/* Pagination */}
            {pagination.totalCount > 0 && (
              <div className="mt-4 border-t pt-4">
                <Pagination
                  page={pagination.page}
                  totalPages={pagination.totalPages}
                  totalCount={pagination.totalCount}
                  pageSize={pagination.pageSize}
                  pageSizeOptions={pageSizeOptions}
                  startIndex={startIndex}
                  endIndex={endIndex}
                  canGoPrev={canGoPrev}
                  canGoNext={canGoNext}
                  onPageChange={goToPage}
                  onPageSizeChange={setPageSize}
                  onFirstPage={firstPage}
                  onLastPage={lastPage}
                  onPrevPage={prevPage}
                  onNextPage={nextPage}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog
        open={showCreateDialog || !!editingGroup}
        onOpenChange={(open) => !open && closeDialogs()}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingGroup ? "Edit Group" : "Create Group"}
            </DialogTitle>
            <DialogDescription>
              {editingGroup
                ? "Update the group details below."
                : "Fill in the details to create a new group."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleFormSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="name">Group Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="e.g., Full Access, Read Only, Reporting Team"
                  required
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="description">Description (optional)</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Describe what this group is for..."
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeDialogs}>
                Cancel
              </Button>
              <Button type="submit" disabled={formSubmitting}>
                {formSubmitting && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                {editingGroup ? "Save Changes" : "Create Group"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Create Dialog */}
      {showBulkCreateDialog && organisationId && (
        <GroupBulkCreateDialog
          organisationId={organisationId}
          onClose={() => setShowBulkCreateDialog(false)}
          onCreated={refresh}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={!!deletingGroup}
        onOpenChange={(open) => !open && setDeletingGroup(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Group?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deletingGroup?.name}"? This
              action cannot be undone. All members will be removed from the
              group and lose the associated feature access.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteSubmitting}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteSubmitting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteSubmitting && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Delete Group
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
